'use client'

import { useState, useEffect } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { Bars3Icon, XMarkIcon, ChartBarIcon, ArrowTrendingUpIcon, CurrencyDollarIcon, CodeBracketIcon } from '@heroicons/react/24/outline'
import { useTheme } from '../../context/ThemeContext'
import './hero.scss'

const navigation = [
  { name: 'Product', href: '#' },
  { name: 'Features', href: '#' },
  { name: 'Marketplace', href: '#' },
  { name: 'Company', href: '#' },
]

// Mock data for live market stats
const marketStats = [
  { label: 'NIFTY 50', value: '22,419.95', trend: 'up', change: '+0.75%' },
  { label: 'SENSEX', value: '73,852.94', trend: 'up', change: '+0.68%' },
  { label: 'Gold', value: '₹6,245.00', trend: 'up', change: '+0.32%' },
]

export const Hero: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const { isDarkMode } = useTheme()

  useEffect(() => {
    console.log('Component mounted, setting isAnimating to true')
    setIsAnimating(true)
  }, [])

  useEffect(() => {
    console.log('isAnimating state changed to:', isAnimating)
  }, [isAnimating])

  return (
    <div className={`relative overflow-hidden ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gradient-to-b from-white to-gray-100 text-gray-900'}`}>
      {/* Candlestick Pattern Background - Partial Coverage */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Top Right - Uptrend Candlesticks (Green) */}
        <div className="absolute top-0 right-0 w-1/3 h-1/3" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20,80 L20,40 M40,70 L40,30 M60,60 L60,20 M80,50 L80,10' stroke='%2322c55e' stroke-width='2' fill='none'/%3E%3Crect x='15' y='40' width='10' height='40' fill='%2322c55e' fill-opacity='0.5'/%3E%3Crect x='35' y='30' width='10' height='40' fill='%2322c55e' fill-opacity='0.5'/%3E%3Crect x='55' y='20' width='10' height='40' fill='%2322c55e' fill-opacity='0.5'/%3E%3Crect x='75' y='10' width='10' height='40' fill='%2322c55e' fill-opacity='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px',
          opacity: 0.4
        }}></div>
        
        {/* Bottom Left - Mixed Trend Candlesticks (Green and Red) */}
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20,100 L20,60 M40,90 L40,50 M60,80 L60,40 M80,70 L80,30 M100,60 L100,20' stroke='%2322c55e' stroke-width='2' fill='none'/%3E%3Crect x='15' y='60' width='10' height='40' fill='%2322c55e' fill-opacity='0.5'/%3E%3Crect x='35' y='50' width='10' height='40' fill='%2322c55e' fill-opacity='0.5'/%3E%3Crect x='55' y='40' width='10' height='40' fill='%2322c55e' fill-opacity='0.5'/%3E%3Crect x='75' y='30' width='10' height='40' fill='%2322c55e' fill-opacity='0.5'/%3E%3Crect x='95' y='20' width='10' height='40' fill='%2322c55e' fill-opacity='0.5'/%3E%3Cpath d='M20,20 L20,60 M40,30 L40,70 M60,40 L60,80 M80,50 L80,90' stroke='%23ef4444' stroke-width='2' fill='none'/%3E%3Crect x='15' y='20' width='10' height='40' fill='%23ef4444' fill-opacity='0.5'/%3E%3Crect x='35' y='30' width='10' height='40' fill='%23ef4444' fill-opacity='0.5'/%3E%3Crect x='55' y='40' width='10' height='40' fill='%23ef4444' fill-opacity='0.5'/%3E%3Crect x='75' y='50' width='10' height='40' fill='%23ef4444' fill-opacity='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '120px 120px',
          opacity: 0.4
        }}></div>
        
        {/* Animated Uptrend Candlesticks on Right Side */}
        <div className={`absolute top-1/2 right-0 w-1/4 h-1/2 overflow-hidden z-50  -translate-y-1/2`}>
          <div className="candles-container">
            <div className="candle candle-1"></div>
            <div className="candle candle-2"></div>
            <div className="candle candle-3"></div>
            <div className="candle candle-4"></div>
            <div className="candle candle-5"></div>
            <div className="candle candle-6"></div>
            <svg className="connecting-line" viewBox="0 0 100 200" preserveAspectRatio="none">
              <path d="M12,40 L28,65 L43,10 L58,90 L72,100 L87,110" stroke="#22c55e" strokeWidth="1" strokeLinecap="round" fill="none" />
            </svg>
          </div>
        </div>
        
        {/* Gradient Overlay for Pattern Section */}
        <div className={`absolute inset-0 bg-gradient-to-br ${isDarkMode ? 'from-gray-900/80 via-gray-900/40' : 'from-white/80 via-white/40'} to-transparent z-40`}></div>
      </div>
      
      <div className="relative px-6 pt-14 lg:px-8 z-30">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-20 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        >
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className={`relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr ${isDarkMode ? 'from-indigo-900 to-purple-900' : 'from-indigo-200 to-purple-200'} opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]`}
          />
        </div>
        <div className="mx-auto max-w-2xl py-16 sm:py-32 lg:py-28">
          <div className="text-center">
            <h1 className="text-5xl font-bold tracking-tight sm:text-7xl bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
              Ticker Tribe
            </h1>
            <p className={`mt-4 text-xl font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Indian Stock Market API
            </p>
            <p className={`mt-6 text-lg leading-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Invest Smarter with comprehensive historical data and automated trading strategies. Everything you need for your investment research.
            </p>
            <p className={`mt-4 text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Discover tools designed to help you make informed investment decisions. Our platform offers seamless integration, robust security, and user-friendly interfaces to ensure you have everything you need at your fingertips.
            </p>
            
            {/* Live Market Stats */}
            <div className="mt-8 flex justify-center gap-8">
              {marketStats.map((stat) => (
                <div key={stat.label} className={`flex flex-col items-center gap-1 p-4 rounded-lg shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{stat.label}</span>
                  <span className={`font-bold text-xl ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{stat.value}</span>
                  <span className={`text-sm font-medium ${stat.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                    {stat.change}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center justify-center gap-x-6">
              <a
                href="#/signup"
                className="rounded-md bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 flex items-center gap-2"
              >
                <CodeBracketIcon className="h-5 w-5" />
                Get API Key
              </a>
              <a 
                href="#/docs" 
                className={`text-sm/6 font-semibold flex items-center gap-2 hover:text-indigo-600 transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
              >
                View Documentation <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
        >
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className={`relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr ${isDarkMode ? 'from-indigo-900 to-purple-900' : 'from-indigo-200 to-purple-200'} opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]`}
          />
        </div>
      </div>
    </div>
  )
}
